import {
  IconBriefcase,
  IconChevronLeft,
  IconChevronRight,
  IconHeart,
  IconHeartbeat,
  IconUserSearch,
} from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaChartBar, FaComments, FaPhoneAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useGuestChat, isGuestChatPath } from "../hooks/useGuestChat";
import AuthModal from "./AuthModal";
import StatsMarquee from "./StatsMarquee";
import styles from "./FeaturesSection.module.css";
// /global card artwork (sourced from the Flutter app assets)
import imgLove from "../assets/global/compatibiltybannerhome.webp";
import imgCareer from "../assets/global/Career.webp";
import imgHealth from "../assets/global/Wellness.webp";
import imgInsights from "../assets/global/Insight.webp";
import imgChat from "../assets/global/Chat.webp";
import imgReports from "../assets/global/Reports.webp";
import imgCall from "../assets/global/Voice.webp";

// --- Hook: Detect mobile ---
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
};

// --- NEW DATA: SEO-Optimized, concise, and engaging content ---
// Each card carries both the bullet-card content (features/buttonText, used on
// home + /market) and the image-card content (subtitle/image, used on /global).
// `image` is "" until real artwork is generated -> placeholder shows.
const allCards = [
  {
    icon: <IconHeart size={32} />,
    title: "Love & Relationships",
    subtitle: "Compatibility, timing & relationship patterns.",
    image: imgLove,
    features: [
      "See your compatibility with others.",
      "Know ideal times for connection.",
      "Understand relationship patterns.",
    ],
    buttonText: "Ask Aura AI",
    path: "/chat",
  },
  {
    icon: <IconBriefcase size={32} />,
    title: "Career & Finance",
    subtitle: "Growth, opportunities & smart timing.",
    image: imgCareer,
    features: [
      "Discover best times for growth.",
      "Spot opportunities aligned with you.",
      "Make informed career choices.",
    ],
    buttonText: "Ask Aura AI",
    path: "/chat",
  },
  {
    icon: <IconHeartbeat size={32} />,
    title: "Health & Wellness",
    subtitle: "Energy cycles & daily balance.",
    image: imgHealth,
    features: [
      "Track energy trends and cycles.",
      "Plan for high or low energy periods.",
      "Stay balanced with daily guidance.",
    ],
    buttonText: "Ask Aura AI",
    path: "/chat",
  },
  {
    icon: <IconUserSearch size={32} />,
    title: "Personal Insights",
    subtitle: "Your karmic path, strengths & purpose.",
    image: imgInsights,
    features: [
      "Decode your karmic path with Aura AI.",
      "Understand your core strengths & weaknesses.",
      "Get deep spiritual astrology insights.",
    ],
    buttonText: "Ask Aura AI",
    path: "/chat",
  },
  {
    icon: <FaComments size={32} />,
    title: "AI Chat",
    subtitle: "Ask anything, get instant answers.",
    image: imgChat,
    features: [
      "Ask questions anytime.",
      "Personalized answers from your chart.",
      "Quick guidance for daily decisions.",
    ],
    buttonText: "Start AI Chat",
    path: "/chat",
  },
  {
    icon: <FaChartBar size={32} />,
    title: "AI Reports",
    subtitle: "Detailed personal astrology reports.",
    image: imgReports,
    features: [
      "Detailed personal astrology reports.",
      "Insights on personality, relationships, timing.",
      "Easy-to-read predictions.",
    ],
    buttonText: "Get Your Report",
    path: "/reports",
  },
  {
    icon: <FaPhoneAlt size={32} />,
    title: "AI Voice Call",
    subtitle: "Talk to your AI astrology guide.",
    image: imgCall,
    features: [
      "Speak with Aura AI, your astrology guide.",
      "Get verbal guidance on key choices.",
      "Personalized insights delivered naturally.",
    ],
    buttonText: "Start AI Call",
    path: "/chat?startCall=1",
  },
];

// --- Animated Prompt Bar Component ---
// Default (Hindi/Hinglish) prompts for the India audience (home + /market).
// Pass `questions` to override per page (e.g. English for /global / USA).
const DEFAULT_QUESTIONS = [
  "When will I get married",
  "Meri job kab lagegi",
  "मेरी शादी में इतनी देरी क्यों हो रही है",
  "Tell me about my career",
  "Meri shadi kab hogi",
  "Aaj ka din kaisa rahega",
];

const AnimatedPromptBar = ({
  questions,
  onAsk,
}: {
  questions: string[];
  onAsk: (question: string) => void;
}) => {
  const [promptIndex, setPromptIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const typingSpeed = 100;
  const deletingSpeed = 50;
  const pauseDuration = 2500;

  // Type-in / type-out (typewriter) effect.
  useEffect(() => {
    const handleTyping = () => {
      const fullText = questions[promptIndex];
      if (isDeleting) {
        setDisplayedText(fullText.substring(0, displayedText.length - 1));
      } else {
        setDisplayedText(fullText.substring(0, displayedText.length + 1));
      }
      if (!isDeleting && displayedText === fullText) {
        setTimeout(() => setIsDeleting(true), pauseDuration);
      } else if (isDeleting && displayedText === "") {
        setIsDeleting(false);
        setPromptIndex((prev) => (prev + 1) % questions.length);
      }
    };
    const timeout = setTimeout(
      handleTyping,
      isDeleting ? deletingSpeed : typingSpeed
    );
    return () => clearTimeout(timeout);
  }, [displayedText, isDeleting, promptIndex]);

  const handlePromptClick = () => {
    onAsk(questions[promptIndex]);
  };

  return (
    <div className={styles.promptBarContainer} onClick={handlePromptClick}>
      <span className={styles.promptText}>{displayedText}</span>
    </div>
  );
};

// --- Main FeaturesSection Component ---
const CARDS_TO_SHOW = 3;

export default function FeaturesSection({
  questions = DEFAULT_QUESTIONS,
  withImages = false,
}: { questions?: string[]; withImages?: boolean } = {}) {
  const isMobile = useIsMobile();
  // /global uses native horizontal scroll on desktop too (no transform slider).
  const scrollMode = isMobile || withImages;
  const [currentIndex, setCurrentIndex] = useState(CARDS_TO_SHOW);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0);
  const mobileTrackRef = useRef(null);
  const timeoutRef = useRef(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const {
    user,
    signInWithFirebaseGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
  } = useAuth();
  const { guestChatEnabled } = useGuestChat();
  const navigate = useNavigate();

  // NEW: Memoize phone auth handlers exactly like in Navbar.tsx
  const phoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestPhoneOtp,
      verifyOtp: verifyPhoneOtp,
    }),
    [requestPhoneOtp, verifyPhoneOtp],
  );

  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp],
  );

  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: verifyMsg91Otp,
    }),
    [verifyMsg91Otp],
  );

  // const navigate = useNavigate();

  // Desktop infinite slider: clones on both ends for a seamless loop
  const desktopSlides = [
    ...allCards.slice(-CARDS_TO_SHOW),
    ...allCards,
    ...allCards.slice(0, CARDS_TO_SHOW),
  ];

  // Desktop auto-advance: slides one whole card every 4.5s
  useEffect(() => {
    if (scrollMode || isPaused || isTransitioning) return;
    timeoutRef.current = setTimeout(() => goNext(), 4500);
    return () => clearTimeout(timeoutRef.current);
  }, [currentIndex, isPaused, isTransitioning, isMobile]);

  const goNext = () => {
    if (isTransitioning) return;
    setCurrentIndex((prev) => prev + 1);
  };
  const goPrev = () => {
    if (isTransitioning) return;
    setCurrentIndex((prev) => prev - 1);
  };

  // Seamless loop: when we land on a clone, jump (no animation) to the real card
  const handleTransitionEnd = () => {
    if (currentIndex >= allCards.length + CARDS_TO_SHOW) {
      setIsTransitioning(true);
      setCurrentIndex(currentIndex - allCards.length);
    } else if (currentIndex <= CARDS_TO_SHOW - 1) {
      setIsTransitioning(true);
      setCurrentIndex(currentIndex + allCards.length);
    }
  };
  useEffect(() => {
    if (isTransitioning) {
      const id = setTimeout(() => setIsTransitioning(false), 50);
      return () => clearTimeout(id);
    }
  }, [isTransitioning]);

  const handleProtectedLinkClick = (path) => {
    if (user) {
      navigate(path);
    } else if (guestChatEnabled && isGuestChatPath(path)) {
      // Non-Indian guest → straight into the chat teaser (ask 1 question, see a
      // blurred answer, sign in to unlock). Indian guests fall through below.
      navigate(path);
    } else {
      setIsAuthModalOpen(true); // Open the OTP modal instead of redirecting
    }
  };

  // MOBILE Scroll Pagination Detection
  useEffect(() => {
    if (!isMobile || !mobileTrackRef.current) return;
    const track = mobileTrackRef.current;
    const handleScroll = () => {
      const scrollLeft = track.scrollLeft;
      const cardWidth = track.clientWidth * 0.8 + 16;
      const index = Math.round(scrollLeft / cardWidth);
      setMobileActiveIndex(index);
    };
    track.addEventListener("scroll", handleScroll);
    return () => track.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  // MOBILE Auto Scroll
  useEffect(() => {
    if (!isMobile || !mobileTrackRef.current) return;
    const track = mobileTrackRef.current;
    const interval = setInterval(() => {
      if (!track) return;
      const nextIndex = (mobileActiveIndex + 1) % allCards.length;
      const cardWidth = track.clientWidth * 0.8 + 16;
      track.scrollTo({ left: nextIndex * cardWidth, behavior: "smooth" });
      setMobileActiveIndex(nextIndex);
    }, 5000);
    return () => clearInterval(interval);
  }, [isMobile, mobileActiveIndex]);

  return (
    // CORRECTED: Wrapped the entire return in a React Fragment <>...</>
    <>
      <section
        className={`${styles.featuresSection}${
          withImages ? ` ${styles.variantImages}` : ""
        }`}
      >
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}>Confused About Life? Ask Aura AI Now</h2>
          <p className={styles.subtitle}>
            Get instant Vedic astrology guidance based on your birth.
          </p>
        </div>

        <StatsMarquee />

        <div
          className={`${styles.carouselContainer}${
            withImages ? ` ${styles.carouselFull}` : ""
          }`}
          onMouseEnter={() => !isMobile && setIsPaused(true)}
          onMouseLeave={() => !isMobile && setIsPaused(false)}
        >
          <div
            className={styles.carouselTrackContainer}
            ref={scrollMode ? mobileTrackRef : null}
          >
            <div
              className={styles.carouselTrack}
              style={
                !scrollMode
                  ? {
                      transform: `translateX(-${
                        (currentIndex * 100) / desktopSlides.length
                      }%)`,
                      transition: isTransitioning
                        ? "none"
                        : "transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)",
                    }
                  : {}
              }
              onTransitionEnd={!scrollMode ? handleTransitionEnd : undefined}
            >
              {(scrollMode ? allCards : desktopSlides).map((card, index) => (
                <div className={styles.cardWrapper} key={index}>
                  {withImages ? (
                    <button
                      type="button"
                      className={styles.imageCard}
                      onClick={() => handleProtectedLinkClick(card.path)}
                    >
                      {card.image ? (
                        <img
                          className={styles.imageCardImg}
                          src={card.image}
                          alt={card.title}
                          loading="lazy"
                        />
                      ) : (
                        <div className={styles.imageCardPh}>{card.icon}</div>
                      )}
                      <div className={styles.imageCardOverlay}>
                        <h3 className={styles.imageCardTitle}>{card.title}</h3>
                        <p className={styles.imageCardSub}>{card.subtitle}</p>
                        <span className={styles.imageCardBtn}>
                          {card.icon}
                          {card.buttonText}
                        </span>
                      </div>
                    </button>
                  ) : (
                    <div className={styles.card}>
                      <div className={styles.cardHeader}>
                        <div className={styles.iconWrapper}>{card.icon}</div>
                        <h3 className={styles.cardTitle}>{card.title}</h3>
                      </div>
                      <ul className={styles.cardList}>
                        {card.features.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                      <div className={styles.ctaContainer}>
                        <button
                          onClick={() => handleProtectedLinkClick(card.path)}
                          className={styles.ctaButton}
                        >
                          {card.buttonText}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {isMobile ? (
            <div className={styles.pagination}>
              {allCards.map((_, index) => (
                <div
                  key={index}
                  className={`${styles.dot} ${
                    index === mobileActiveIndex ? styles.activeDot : ""
                  }`}
                />
              ))}
            </div>
          ) : withImages ? null : (
            <>
              <button
                className={`${styles.arrow} ${styles.arrowLeft}`}
                onClick={goPrev}
              >
                <IconChevronLeft size={24} />
              </button>
              <button
                className={`${styles.arrow} ${styles.arrowRight}`}
                onClick={goNext}
              >
                <IconChevronRight size={24} />
              </button>
            </>
          )}
        </div>
      </section>

      {/* This now works correctly because it's inside the Fragment */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onGoogleSignIn={signInWithFirebaseGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
    </>
  );
}
